import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import config from '../config';
import { useAuth } from '../context/AuthContext';
import './DocumentViewer.css';

const DocumentViewer = ({ isOpen, onClose, sourceMetadata }) => {
  const [markdownContent, setMarkdownContent] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeView, setActiveView] = useState('pdf'); // 'pdf' or 'markdown'
  const [currentPage, setCurrentPage] = useState(1);
  const [canToggle, setCanToggle] = useState(false);
  
  const { getAuthHeaders, user } = useAuth();
  const canShowPdf = user?.show_pdf !== false;
  const contentRef = useRef(null);
  const observerRef = useRef(null);

  // Parse markdown content into pages
  const pages = useMemo(() => {
    if (!markdownContent) return [];
    
    // Split by page break markers: <!-- PAGE_BREAK:X -->
    const pageBreakRegex = /<!-- PAGE_BREAK:(\d+) -->/g;
    const parts = markdownContent.split(pageBreakRegex);
    
    // parts will be: [content before first marker, pageNum1, content1, pageNum2, content2, ...]
    const parsedPages = [];
    
    // If no markers found, treat entire content as single page
    if (parts.length === 1) {
      // Check for legacy format: **Page X** markers
      const legacyRegex = /\n\n---\n\n\*\*Page\s+(\d+)\*\*\n\n---\n\n/g;
      const legacyParts = markdownContent.split(legacyRegex);
      
      if (legacyParts.length > 1) {
        for (let i = 1; i < legacyParts.length; i += 2) {
          const pageNum = parseInt(legacyParts[i], 10);
          const content = legacyParts[i + 1] || '';
          if (content.trim()) {
            parsedPages.push({ pageNum, content: content.trim() });
          }
        }
      } else {
        // No markers at all - single page document
        parsedPages.push({ pageNum: 1, content: markdownContent });
      }
    } else {
      // Process new format markers
      for (let i = 1; i < parts.length; i += 2) {
        const pageNum = parseInt(parts[i], 10);
        const content = parts[i + 1] || '';
        if (content.trim()) {
          parsedPages.push({ pageNum, content: content.trim() });
        }
      }
    }
    
    return parsedPages;
  }, [markdownContent]);

  const pageCount = pages.length;

  useEffect(() => {
    if (isOpen && sourceMetadata?.source) {
      fetchDocumentContent();
    } else {
      resetState();
    }
  }, [isOpen, sourceMetadata]);

  const resetState = () => {
    setMarkdownContent(null);
    setPdfUrl(null);
    setError(null);
    setActiveView('markdown');
    setCurrentPage(1);
    setCanToggle(false);
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
  };

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Set up IntersectionObserver to track current page
  useEffect(() => {
    if (!isOpen || activeView !== 'markdown' || pages.length === 0) return;

    const setupObserver = () => {
      const contentEl = contentRef.current;
      if (!contentEl) return;

      const pageElements = contentEl.querySelectorAll('.document-page');
      if (pageElements.length === 0) return;

      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      observerRef.current = new IntersectionObserver(
        (entries) => {
          // Find the page that is most visible
          let mostVisiblePage = null;
          let maxVisibility = 0;

          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio > maxVisibility) {
              maxVisibility = entry.intersectionRatio;
              mostVisiblePage = entry.target;
            }
          });

          // Fallback: find page closest to top of viewport
          if (!mostVisiblePage) {
            const containerRect = contentEl.getBoundingClientRect();
            let closest = null;
            let closestDistance = Infinity;

            pageElements.forEach((page) => {
              const rect = page.getBoundingClientRect();
              const distance = Math.abs(rect.top - containerRect.top);
              if (rect.top <= containerRect.top + 150 && distance < closestDistance) {
                closestDistance = distance;
                closest = page;
              }
            });

            if (closest) mostVisiblePage = closest;
          }

          if (mostVisiblePage) {
            const pageNum = parseInt(mostVisiblePage.dataset.page, 10);
            if (!isNaN(pageNum) && pageNum !== currentPage) {
              setCurrentPage(pageNum);
            }
          }
        },
        {
          root: contentEl,
          rootMargin: '-5% 0px -60% 0px',
          threshold: [0, 0.1, 0.25, 0.5, 0.75, 1]
        }
      );

      pageElements.forEach((page) => {
        observerRef.current.observe(page);
      });
    };

    const timeoutId = setTimeout(setupObserver, 150);

    return () => {
      clearTimeout(timeoutId);
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [isOpen, activeView, pages, currentPage]);

  const fetchDocumentContent = async () => {
    setLoading(true);
    setError(null);
    try {
      const filename = sourceMetadata.source.split('/').pop();
      const authHeaders = getAuthHeaders();
      
      const response = await fetch(`${config.API_BASE_URL}/document/content?filename=${encodeURIComponent(filename)}`, {
        headers: authHeaders
      });

      if (!response.ok) {
        throw new Error('Failed to load document');
      }

      const data = await response.json();
      
      if (data.markdown_content) {
        setMarkdownContent(data.markdown_content);
      }
      if (data.pdf_url) {
        setPdfUrl(data.pdf_url);
      }

      const hasMarkdown = !!data.markdown_content;
      const hasPdf = !!data.pdf_url && canShowPdf;
      setCanToggle(hasMarkdown && hasPdf);

      // Default to PDF view when available (shows original document)
      // Fall back to markdown if PDF not available or user cannot view PDFs
      if (hasPdf) {
        setActiveView('pdf');
      } else if (hasMarkdown) {
        setActiveView('markdown');
      } else {
        // Handle legacy API response format
        if (data.type === 'markdown' && data.content) {
          setMarkdownContent(data.content);
          setActiveView('markdown');
        } else if (data.type === 'pdf' && data.url) {
          setPdfUrl(data.url);
          setActiveView('pdf');
        } else {
          throw new Error('Unknown document format');
        }
      }
    } catch (err) {
      console.error("Error fetching document:", err);
      setError(err.message || "Could not load document.");
    } finally {
      setLoading(false);
    }
  };

  const handleViewToggle = useCallback((view) => {
    if (view === activeView || !canToggle) return;
    setActiveView(view);
  }, [activeView, canToggle]);

  // Custom components for markdown rendering
  const markdownComponents = {
    img: ({node, ...props}) => (
      <div className="doc-image-container">
        <img {...props} loading="lazy" className="doc-image" alt={props.alt || "Document image"} />
      </div>
    ),
    table: ({ node, children, ...props }) => (
      <div className="table-responsive">
        <table {...props}>{children}</table>
      </div>
    ),
  };

  // Get PDF URL with page fragment
  const getPdfUrlWithPage = () => {
    if (!pdfUrl) return '';
    return `${pdfUrl}#page=${currentPage}`;
  };

  if (!isOpen) return null;

  const title = sourceMetadata?.document_title || sourceMetadata?.source || 'Document';

  return (
    <div className="document-viewer-overlay" onClick={onClose}>
      <div className="document-viewer-modal" onClick={e => e.stopPropagation()}>
        <div className="document-viewer-header">
          <div className="document-viewer-title">
            <span className="document-icon">📄</span>
            <span title={title}>{title}</span>
          </div>
          
          {/* View Toggle */}
          {canToggle && !loading && !error && (
            <div className="view-toggle">
              <button 
                className={`view-toggle-btn ${activeView === 'pdf' ? 'active' : ''}`}
                onClick={() => handleViewToggle('pdf')}
                title="View original PDF document"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <path d="M10 12h4"></path>
                  <path d="M10 16h4"></path>
                  <path d="M10 8h1"></path>
                </svg>
                <span>Original</span>
              </button>
              <button 
                className={`view-toggle-btn ${activeView === 'markdown' ? 'active' : ''}`}
                onClick={() => handleViewToggle('markdown')}
                title="View as searchable text"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                  <polyline points="10 9 9 9 8 9"></polyline>
                </svg>
                <span>Text</span>
              </button>
            </div>
          )}
          
          {/* Page indicator */}
          {pageCount > 0 && !loading && !error && activeView === 'markdown' && (
            <div className="page-indicator">
              Page {currentPage} of {pageCount}
            </div>
          )}
          
          <button className="document-viewer-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        
        <div className="document-viewer-content" ref={contentRef}>
          {loading ? (
            <div className="document-loading">
              <div className="loading-spinner"></div>
              <p>Loading full document...</p>
            </div>
          ) : error ? (
            <div className="document-error">
              <div className="error-icon">⚠️</div>
              <p>{error}</p>
              <button onClick={fetchDocumentContent}>Try Again</button>
            </div>
          ) : activeView === 'markdown' && pages.length > 0 ? (
            <div className="pages-container">
              {pages.map((page, index) => (
                <div 
                  key={page.pageNum} 
                  className="document-page"
                  data-page={page.pageNum}
                >
                  <div className="page-number-badge">
                    {page.pageNum}
                  </div>
                  <div className="page-content">
                    <Markdown 
                      remarkPlugins={[remarkGfm, remarkMath]} 
                      rehypePlugins={[rehypeKatex]}
                      components={markdownComponents}
                    >
                      {page.content}
                    </Markdown>
                  </div>
                </div>
              ))}
            </div>
          ) : activeView === 'pdf' && pdfUrl && canShowPdf ? (
            <iframe 
              src={getPdfUrlWithPage()} 
              className="pdf-frame" 
              title="PDF Document"
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default DocumentViewer;
